import { Navigate, Outlet, createBrowserRouter } from 'react-router';
import { AuthGuard } from './AuthGuard';
import { OnboardingGuard } from './OnboardingGuard';
import PageWrapper from './page-wrapper';
import RootLayout from './root-layout';
import AuthLayout from '@/pages/auth/layout';
import DashboardLayout from '@/pages/dashboard/layout';
import { useAuthStore } from '@/store/useAuthStore';
import React from 'react';
import Demo from '@/pages/public/demo';
import AiEmailAssistantPage from '@/pages/ai-email-assistant';
import GeneratedEmailPage from '@/pages/ai-email-assistant/generated-email/page';

// Lazy load components
const Login = React.lazy(() => import('@/pages/auth/login/page'));
const ConfirmPassword = React.lazy(() => import('@/pages/auth/confirm-password/page'));
const Emailverification = React.lazy(() => import('@/pages/auth/email-verification/page'));
const ForgotPassword = React.lazy(() => import('@/pages/auth/forgot-password/page'));
const Register = React.lazy(() => import('@/pages/auth/register/page'));
const BlockedAccount = React.lazy(() => import('@/pages/block-user/page'));
const Dashboard = React.lazy(() => import('@/pages/dashboard/dashboard/page'));
const Practices = React.lazy(() => import('@/pages/dashboard/practices/page'));
const Settings = React.lazy(() => import('@/pages/dashboard/settings/page'));
const PrivacySafetyTerms = React.lazy(() => import('@/pages/dashboard/privacy-safety-terms/page'));
const TeamManagement = React.lazy(() => import('@/pages/dashboard/team-management/page'));
const TeamOverview = React.lazy(() => import('@/pages/dashboard/team-overview/page'));
const DonorScenarios = React.lazy(() => import('@/pages/dashboard/donor-scenarios/page'));
const CardSetting = React.lazy(() => import('@/pages/dashboard/cardSetting/page'));
const NotFound = React.lazy(() => import('@/pages/not-found/page'));
const Help = React.lazy(() => import('@/pages/help/page'));
const Donors = React.lazy(() => import('@/pages/dashboard/donors/page'));
const SimulationHistory = React.lazy(() => import('@/pages/dashboard/donors/SimulationHistory'));
const JoinPage = React.lazy(() => import('@/pages/joinTeam/page'));
const Transcript = React.lazy(() => import('@/pages/transcript/page'));
const AddOnPayment = React.lazy(() => import('@/pages/dashboard/addon-payment/page'));
const Payment = React.lazy(() => import('@/pages/dashboard/payment/page'));
const Onboarding = React.lazy(() => import('@/pages/onboarding/page'));
const DonorsChat = React.lazy(() => import('@/pages/donors-chat/page'));
const Coaching = React.lazy(() => import('@/pages/coaching/page'));
const Feedbacks = React.lazy(() => import('@/pages/feedbacks/page'));
const Simulation = React.lazy(() => import('@/pages/simulation/page'));
const DetailedFeedback = React.lazy(() => import('@/pages/donor-simulation/detailed-feedback/page'));
const SimulationSelectDonors = React.lazy(() => import('@/pages/simulation-select-donors/page'));
const DetailedConversationSummary = React.lazy(
  () => import('@/pages/coach-tivy-conversation/detailed-conversation-summary/page')
);
const TermCondition = React.lazy(() => import('@/pages/term-condition/page'));
const Subscription = React.lazy(() => import('@/pages/subscription/page'));
const Logout = React.lazy(() => import('@/pages/auth/logout/page'));
const ResetLinkPage = React.lazy(() => import('@/pages/reset-link/page'));
const Analytics = React.lazy(() => import('@/pages/analytics/page'));

const isAuthenticated = useAuthStore.getState().isAuthenticated;
const user = useAuthStore.getState().user;

const router = createBrowserRouter([
  {
    path: '/demo',
    element: <Demo />
  },
  {
    element: <RootLayout />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          {
            path: '/login',
            element: <Login />,
            handle: { title: 'Login' }
          },
          {
            path: '/confirm-password',
            element: <ConfirmPassword />,
            handle: { title: 'Confirm Password' }
          },
          {
            path: '/email-verification',
            element: <Emailverification />,
            handle: { title: 'Email Verification' }
          },
          {
            path: '/forgot-password',
            element: <ForgotPassword />,
            handle: { title: 'Forgot Password' }
          },
          {
            path: '/register',
            element: <Register />,
            handle: { title: 'Register' }
          },
          {
            path: '/block-user',
            element: <BlockedAccount />,
            handle: { title: 'Blocked Account' }
          }
        ]
      },
      {
        element: (
          <AuthGuard>
            <DashboardLayout isDashboard={true} />
          </AuthGuard>
        ),
        children: [
          {
            path: '/dashboard',
            element: <Dashboard />,
            handle: { title: 'Dashboard' }
          },
          {
            path: '/practices',
            element: <Practices />,
            handle: { title: 'Past Conversations' }
          },
          {
            path: '/settings',
            element: <Settings />,
            handle: { title: 'Settings' }
          },
          {
            path: '/privacy-safety-terms',
            element: <PrivacySafetyTerms />,
            handle: { title: 'Privacy & Terms' }
          },
          {
            path: '/team-management',
            element: <TeamManagement />,
            handle: { title: 'Team Management' }
          },
          {
            path: '/team-overview',
            element: <TeamOverview />,
            handle: { title: 'Team Overview' }
          },
          {
            path: '/donor-scenarios',
            element: <DonorScenarios />,
            handle: { title: 'Practice Scenarios' }
          },
          {
            path: '/cardSetting',
            element: <CardSetting />,
            handle: { title: 'Card Settings' }
          },
          {
            path: '/not-found',
            element: <NotFound />,
            handle: { title: 'Not Found' }
          },
          {
            path: '/help',
            element: <Help />,
            handle: { title: 'Help' }
          },
          {
            path: '/donors',
            element: <Donors />,
            handle: { title: 'Donors' }
          },
          {
            path: '/simulation-history/:donorId',
            element: <SimulationHistory />,
            handle: { title: 'Simulation History' }
          },
          {
            path: '/ai-email-assistant',
            element: <AiEmailAssistantPage />,
            handle: { title: 'Tivy Email Assistant', hideHeader: true }
          },
          {
            path: '/ai-email-assistant/generated-email',
            element: <GeneratedEmailPage />,
            handle: { title: 'Tivy Email Assistant' }
          }
        ]
      },
      {
        element: (
          <AuthGuard>
            <PageWrapper onRouteChange>
              <Outlet />
            </PageWrapper>
          </AuthGuard>
        ),
        children: [
          {
            path: '/joinTeam/:joinLink',
            element: <JoinPage />,
            handle: { title: 'Join Team' }
          },
          {
            path: '/transcript',
            element: <Transcript />,
            handle: { title: 'Transcript' }
          },
          {
            path: '/addon-payment',
            element: <AddOnPayment />,
            handle: { title: 'Add-on Payment' }
          },
          {
            path: '/payment',
            element: <Payment />,
            handle: { title: 'Payment' }
          }
        ]
      },
      {
        element: (
          <OnboardingGuard>
            <PageWrapper onRouteChange>
              <Outlet />
            </PageWrapper>
          </OnboardingGuard>
        ),
        children: [
          {
            path: '/onboarding',
            element: <Onboarding />,
            handle: { title: 'Onboarding' }
          }
        ]
      },
      {
        element: (
          <AuthGuard>
            <DashboardLayout isDashboard={false} />
          </AuthGuard>
        ),
        children: [
          {
            path: '/progress',
            element: <Analytics />,
            handle: { title: 'Your Progress' }
          },
          {
            path: '/donors-chat',
            element: <DonorsChat />,
            handle: {
              title: 'Chat Simulator',
              hideHeader: true
            }
          },
          {
            path: '/coaching',
            element: <Coaching />,
            handle: { title: 'Coaching with Tivy', hideHeader: true }
          },
          {
            path: '/feedbacks',
            element: <Feedbacks />,
            handle: { title: 'Feedbacks' }
          },
          {
            path: '/simulation',
            element: <Simulation />,
            handle: { title: 'Simulation', hideHeader: true }
          },
          {
            path: '/detailed-feedback',
            element: <DetailedFeedback />,
            handle: { title: 'Detailed Feedback', hideHeader: true }
          },
          {
            path: '/simulation-select-donors',
            element: <SimulationSelectDonors />,
            handle: { title: 'Select Donors' }
          },
          {
            path: '/detailed-conversation-summary',
            element: <DetailedConversationSummary />,
            handle: { title: 'Conversation Summary', hideHeader: true }
          }
        ]
      },
      {
        path: '/term-condition',
        element: (
          <PageWrapper onRouteChange isTransition>
            <TermCondition />
          </PageWrapper>
        ),
        handle: { title: 'Terms & Conditions' }
      },
      {
        path: '/subscription',
        element: (
          <PageWrapper onRouteChange isTransition>
            <Subscription />
          </PageWrapper>
        ),
        handle: { title: 'Subscription' }
      },
      {
        path: '/logout',
        element: <Logout />,
        handle: { title: 'Logout' }
      },
      {
        path: '/reset-link/*',
        element: <ResetLinkPage />,
        handle: { title: 'Reset Password' }
      },
      {
        path: '*',
        element: !isAuthenticated || !user?.isVerified ? <Navigate to={'/login'} /> : <Navigate to={'/dashboard'} />
      }
    ]
  }
]);

export default router;
